import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MaintenanceService } from '../../../services/maintenance.service';
import { AuthService } from '../../../services/auth/auth.service';
import { StaffCanDirective } from '../../../shared/staff-can.directive';
import { BusinessMaintenance, MAINTENANCE_DEFAULT_MESSAGE, MAINTENANCE_MESSAGE_MAX } from '../../../models/maintenance.model';
import { expectedEndProblem, toLocalInput, toRequest } from '../../system/maintenance-page/maintenance-form';

/**
 * Business profile > Access (specs/maintenance): whether this business is under maintenance, who closed it and when,
 * and the buttons to close it, change the words or lift it. Follows the profile's edit lease like every other change
 * on the page, and asks before closing. Shown to holders of Platform.Maintenance.Manage.
 */
@Component({
  selector: 'app-business-maintenance-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, StaffCanDirective],
  templateUrl: './business-maintenance-panel.component.html',
  styleUrls: ['./business-maintenance-panel.component.css']
})
export class BusinessMaintenancePanelComponent implements OnChanges {
  @Input() businessId: string | null | undefined = null;
  @Input() businessName: string | null | undefined = null;
  /** The profile's edit lease: the buttons wait for it like every other change here. */
  @Input() editing = false;

  readonly defaultMessage = MAINTENANCE_DEFAULT_MESSAGE;
  readonly messageMax = MAINTENANCE_MESSAGE_MAX;

  current: BusinessMaintenance | null = null;
  loading = false;
  /** Set when the state could not be read (403, or 404 before this release): hides the buttons. */
  unavailable = '';
  error = '';
  notice = '';

  formOpen = false;
  liftAsking = false;
  message = '';
  expectedEndLocal = '';
  saving = false;

  constructor(private maintenance: MaintenanceService, private auth: AuthService) {}

  get canManage(): boolean { return this.auth.can('Platform.Maintenance.Manage'); }
  get expectedEndError(): string { return expectedEndProblem(this.expectedEndLocal); }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['businessId']) {
      this.reset();
      this.load();
    }
    if (changes['editing'] && !this.editing) {
      // Leaving edit mode closes anything half-done, like the rest of the profile.
      this.formOpen = false;
      this.liftAsking = false;
    }
  }

  load(): void {
    const id = this.businessId;
    if (!id || !this.canManage) return;
    this.loading = true;
    this.unavailable = '';
    this.maintenance.getBusiness(id).subscribe({
      next: row => {
        this.loading = false;
        this.current = row ?? null;
      },
      error: (err: HttpErrorResponse) => {
        this.loading = false;
        this.current = null;
        this.unavailable = err?.status === 404
          ? 'The account API has no maintenance mode yet.'
          : err?.status === 403 ? 'Only a SuperAdmin may read or change maintenance.' : 'Could not read the maintenance state.';
      }
    });
  }

  openForm(): void {
    this.error = '';
    this.notice = '';
    this.liftAsking = false;
    this.message = this.current?.message ?? '';
    this.expectedEndLocal = toLocalInput(this.current?.expectedEndUtc);
    this.formOpen = true;
  }

  cancelForm(): void { this.formOpen = false; }

  confirmForm(): void {
    const id = this.businessId;
    if (!id || this.saving || this.expectedEndError) return;
    const rewording = !!this.current;
    this.saving = true;
    this.error = '';
    this.maintenance.setBusiness(id, toRequest(this.message, this.expectedEndLocal)).subscribe({
      next: saved => {
        this.saving = false;
        this.formOpen = false;
        this.current = saved;
        this.notice = rewording
          ? 'Words saved.'
          : 'This business is under maintenance on every channel. Its tills and phones see the page within seconds.';
      },
      error: (err: HttpErrorResponse) => {
        this.saving = false;
        this.error = err?.error?.message || 'Could not put this business under maintenance.';
      }
    });
  }

  askLift(): void {
    this.error = '';
    this.notice = '';
    this.formOpen = false;
    this.liftAsking = true;
  }

  cancelLift(): void { this.liftAsking = false; }

  confirmLift(): void {
    const id = this.businessId;
    if (!id || this.saving) return;
    this.saving = true;
    this.error = '';
    this.maintenance.clearBusiness(id).subscribe({
      next: () => {
        this.saving = false;
        this.liftAsking = false;
        this.current = null;
        this.notice = 'Maintenance lifted. Tills and phones go straight back in.';
      },
      error: (err: HttpErrorResponse) => {
        this.saving = false;
        this.error = err?.error?.message || 'Could not lift maintenance.';
      }
    });
  }

  utc(value: string | null | undefined): string | null {
    if (!value) return null;
    return /(Z|[+-]\d{2}:?\d{2})$/i.test(value) ? value : `${value}Z`;
  }

  private reset(): void {
    this.current = null;
    this.error = '';
    this.notice = '';
    this.unavailable = '';
    this.formOpen = false;
    this.liftAsking = false;
  }
}
